import React, { useState } from 'react'
import { FiCalendar, FiMapPin, FiUsers, FiPackage, FiEdit2, FiTrash2, FiEye } from 'react-icons/fi'
import { IoIosCloseCircleOutline } from 'react-icons/io'
import { toast } from 'react-toastify'
import useSWR from 'swr'
import api, { fetcher } from '../../utils/api'
import Spinner from '../../components/Spinner'
import CommodityActivityModal from './CommodityActivityModal'
import DeleteModal from '../../components/DeleteModal'
import FarmerDetailDrawer from '../../components/Drawers/FarmerDetailDrawer'
import {
  canModifyActivity,
  calculateActivityProgress,
  isActivityEnded,
} from '../../utils/activityUtils'

const ActivityDetail = ({ activity, onClose, onEdit, onManageFarmers }) => {
  const [showCommodityModal, setShowCommodityModal] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [showFarmerDrawer, setShowFarmerDrawer] = useState(false)
  const [selectedFarmer, setSelectedFarmer] = useState(null)

  // Fetch activity details using SWR
  const { data: activityDetails, isLoading, error, mutate } = useSWR(
    activity?.id ? `activities/${activity.id}/` : null,
    fetcher
  )

  // Use the fetched data or fallback to the passed activity prop
  const currentActivity = activityDetails || activity
  const modifiable = canModifyActivity(currentActivity)
  const progress = calculateActivityProgress(
    currentActivity?.start_date,
    currentActivity?.end_date
  )
  const ended = isActivityEnded(currentActivity)

  const handleDeleteActivity = async () => {
    setDeleteLoading(true)
    try {
      await api.delete(`activities/${currentActivity?.id}/`)
      toast.success('Activity deleted successfully')
      setShowConfirmModal(false)
      onClose()
    } catch (error) {
      toast.error('Failed to delete activity')
      console.error('Delete error:', error)
    } finally {
      setDeleteLoading(false)
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'planned':
        return 'bg-yellow-100 text-yellow-800'
      case 'ongoing':
        return 'bg-green-100 text-green-800'
      case 'completed':
        return 'bg-blue-100 text-blue-800'
      case 'cancelled':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] backdrop-blur-md">
        <div className="bg-white dark:bg-dark rounded-lg shadow-lg w-full md:max-w-4xl relative max-h-[90vh] flex flex-col overflow-hidden">
          <div className='flex flex-row justify-between items-center border-b border-gray-200 p-4 flex-shrink-0 dark:border-border-dark'>
            <h2 className='text-xl font-bold dark:text-white'>Activity Details</h2>
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <IoIosCloseCircleOutline className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 flex items-center justify-center">
            <Spinner />
          </div>
        </div>
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] backdrop-blur-md">
        <div className="bg-white dark:bg-dark rounded-lg shadow-lg w-full md:max-w-4xl relative max-h-[90vh] flex flex-col overflow-hidden">
          <div className='flex flex-row justify-between items-center border-b border-gray-200 p-4 flex-shrink-0 dark:border-border-dark'>
            <h2 className='text-xl font-bold dark:text-white'>Activity Details</h2>
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <IoIosCloseCircleOutline className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 flex items-center justify-center">
            <div className="text-center">
              <p className="text-red-600 mb-4">Failed to load activity details</p>
              <button
                onClick={() => mutate()}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] backdrop-blur-md">
      <div className="bg-white dark:bg-dark rounded-lg shadow-lg w-full md:max-w-5xl relative max-h-[90vh] flex flex-col overflow-hidden">
        <div className='flex flex-row justify-between items-center border-b border-gray-200 p-4 flex-shrink-0 dark:border-border-dark'>
          <h2 className='text-xl font-bold dark:text-white'>
            Activity Details
          </h2>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <IoIosCloseCircleOutline className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {/* Header Section */}
          <div className="mb-6">
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  {currentActivity?.name}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-lg">
                  {currentActivity?.description}
                </p>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(currentActivity?.status)}`}>
                {currentActivity?.status_display || currentActivity?.status?.charAt(0).toUpperCase() + currentActivity?.status?.slice(1)}
              </span>
            </div>
          </div>

          {/* Activity Information Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Basic Information
              </h4>

              <div className="space-y-3">
                <div className="flex items-center">
                  <FiPackage className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Activity Type</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {currentActivity?.activity_type_display || currentActivity?.activity_type || 'Other'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center">
                  <FiCalendar className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Duration</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {formatDate(currentActivity?.start_date)} - {formatDate(currentActivity?.end_date)}
                      {currentActivity?.duration_days && (
                        <span className="text-sm text-gray-500 ml-2">({currentActivity?.duration_days} days)</span>
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-center">
                  <FiUsers className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Farmers Assigned</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {currentActivity?.farmers_count || currentActivity?.farmers?.length || 0} farmers
                    </p>
                  </div>
                </div>

                <div className="flex items-center">
                  <FiPackage className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Commodities</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {currentActivity?.commodities_count || currentActivity?.commodities?.length || 0} commodities
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Location Information */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Location
              </h4>

              <div className="space-y-3">
                <div className="flex items-center">
                  <FiMapPin className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Country</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {currentActivity?.country || 'Not specified'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center">
                  <FiMapPin className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Region</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {currentActivity?.region || 'Not specified'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center">
                  <FiMapPin className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">District</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {currentActivity?.district || 'Not specified'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center">
                  <FiMapPin className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Community</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {currentActivity?.community || 'Not specified'}
                    </p>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {ended && (
            <div className="mb-6 px-4 py-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-200">
              This activity has ended. Updates and farmer assignments are no longer allowed.
            </div>
          )}
          {currentActivity?.created_by && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Created By
              </h4>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="flex items-center space-x-4">
                  {currentActivity?.created_by?.photo_url ? (
                    <img
                      src={currentActivity?.created_by?.photo_url}
                      alt={`${currentActivity?.created_by?.firstname} ${currentActivity?.created_by?.lastname}`}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                      <span className="text-gray-600 dark:text-gray-300 font-medium">
                        {currentActivity?.created_by?.firstname?.charAt(0)}{currentActivity?.created_by?.lastname?.charAt(0)}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {currentActivity?.created_by?.firstname} {currentActivity?.created_by?.lastname}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {currentActivity?.created_by?.email}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {currentActivity?.created_by?.phone}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Activity Metadata */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Activity Metadata
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Created</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {currentActivity?.date_created ? new Date(currentActivity?.date_created).toLocaleString() : 'Not available'}
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Last Modified</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {currentActivity?.date_modified ? new Date(currentActivity?.date_modified).toLocaleString() : 'Not available'}
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Status</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {currentActivity?.is_active ? 'Active' : 'Inactive'}
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Activity ID</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  #{currentActivity?.id}
                </p>
              </div>
            </div>
          </div>

          {/* Detailed Farmers Information */}
          {currentActivity?.farmers && currentActivity?.farmers.length > 0 && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Assigned Farmers ({currentActivity?.farmers.length})
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {currentActivity?.farmers.map((farmer) => (
                  <div key={farmer?.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 relative group">
                    <div className="flex items-start space-x-3">
                      {farmer?.profile_image ? (
                        <img
                          src={farmer?.profile_image}
                          alt={`${farmer?.firstname} ${farmer?.lastname}`}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                          <span className="text-gray-600 dark:text-gray-300 font-medium">
                            {farmer?.firstname?.charAt(0)}{farmer?.lastname?.charAt(0)}
                          </span>
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h5 className="font-medium text-gray-900 dark:text-white">
                            {farmer?.full_name || `${farmer?.firstname} ${farmer?.lastname}`}
                          </h5>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setSelectedFarmer(farmer)
                              setShowFarmerDrawer(true)
                            }}
                            className="p-2 text-gray-500 hover:text-yellowcolor dark:text-gray-400 dark:hover:text-yellowcolor transition-colors"
                            title="View Farmer Details"
                          >
                            <FiEye size={18} />
                          </button>
                        </div>
                        <div className="mt-2 space-y-1">
                          {farmer?.email && (
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              📧 {farmer?.email}
                            </p>
                          )}
                          {farmer?.phone && (
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              📞 {farmer?.phone}
                            </p>
                          )}
                          {farmer?.region && (
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              📍 {farmer?.region}, {farmer?.district}
                            </p>
                          )}
                          <div className="flex items-center space-x-2 mt-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${farmer?.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                              {farmer?.is_active ? 'Active' : 'Inactive'}
                            </span>
                            {farmer?.farms_count > 0 && (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {farmer?.farms_count} farms
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Commodities Information */}
          {currentActivity?.commodities && currentActivity?.commodities?.length > 0 && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Related Commodities ({currentActivity?.commodities?.length})
              </h4>
              <div className="flex flex-wrap gap-2">
                {currentActivity?.commodities?.map((commodity, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium"
                  >
                    {commodity?.name || commodity}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Activity Progress */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Activity Progress
            </h4>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Progress</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {progress.percent}%
                    {progress.totalDays > 0 && (
                      <span className="ml-1">
                        (Day {progress.daysElapsed} of {progress.totalDays})
                      </span>
                    )}
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      progress.phase === 'ended' ? 'bg-green-500' :
                      progress.phase === 'ongoing' ? 'bg-yellow-500' : 'bg-gray-400'
                    }`}
                    style={{ width: `${progress.percent}%` }}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center">
                  <div className={`w-4 h-4 rounded-full mr-4 ${
                    progress.percent > 0 || progress.phase === 'ended' ? 'bg-green-500' : 'bg-gray-300'
                  }`} />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-white">Activity Start</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(currentActivity?.start_date)}
                    </p>
                  </div>
                </div>

                {progress.phase === 'ongoing' && (
                  <div className="flex items-center">
                    <div className="w-4 h-4 rounded-full mr-4 bg-yellow-500 animate-pulse" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-white">In Progress</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {progress.percent}% complete based on schedule
                      </p>
                    </div>
                  </div>
                )}

                {progress.phase === 'upcoming' && (
                  <div className="flex items-center">
                    <div className="w-4 h-4 rounded-full mr-4 bg-gray-300" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-white">Not Started Yet</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Starts on {formatDate(currentActivity?.start_date)}
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex items-center">
                  <div className={`w-4 h-4 rounded-full mr-4 ${
                    progress.phase === 'ended' ? 'bg-green-500' : 'bg-gray-300'
                  }`} />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-white">Activity End</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(currentActivity?.end_date)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Activity Statistics */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Activity Statistics
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-500 rounded-lg">
                    <FiUsers className="w-5 h-5 text-white" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-600 dark:text-blue-400">Farmers</p>
                    <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                      {currentActivity?.farmers_count || currentActivity?.farmers?.length || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="p-2 bg-green-500 rounded-lg">
                    <FiPackage className="w-5 h-5 text-white" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-green-600 dark:text-green-400">Commodities</p>
                    <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                      {currentActivity?.commodities_count || currentActivity?.commodities?.length || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-500 rounded-lg">
                    <FiCalendar className="w-5 h-5 text-white" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-purple-600 dark:text-purple-400">Duration</p>
                    <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                      {currentActivity?.duration_days || 0} days
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="pt-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            {/* Mobile Layout */}
            <div className="flex flex-col space-y-3 lg:hidden">
              {modifiable && <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => onManageFarmers(currentActivity)}
                  className="flex items-center justify-center px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <FiUsers className="mr-1" />
                  <span className="hidden sm:inline">Manage Farmers</span>
                  <span className="sm:hidden">Farmers</span>
                </button>
                <button
                  onClick={() => setShowCommodityModal(true)}
                  className="flex items-center justify-center px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <FiPackage className="mr-1" />
                  <span className="hidden sm:inline">Manage Commodities</span>
                  <span className="sm:hidden">Commodities</span>
                </button>
              </div>}
              <div className="grid grid-cols-2 gap-2">
                {modifiable && <button
                  onClick={() => onEdit(currentActivity)}
                  className="flex items-center justify-center px-3 py-2 text-sm bg-yellowcolor text-black rounded-lg hover:bg-yellowcolor/90 transition-colors"
                >
                  <FiEdit2 className="mr-1" />
                  Edit
                </button>}
                <button
                  onClick={() => setShowConfirmModal(true)}
                  className="flex items-center justify-center px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <FiTrash2 className="mr-1" />
                  Delete
                </button>
              </div>
              <button
                onClick={onClose}
                className="w-full px-3 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-dark dark:text-gray-300 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>

            {/* Desktop Layout */}
            <div className="hidden lg:flex justify-between items-center">
              {modifiable && <div className="flex space-x-2">
                <button
                  onClick={() => onManageFarmers(currentActivity)}
                  className="flex items-center px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <FiUsers className="mr-1" />
                  Manage Farmers
                </button>
                <button
                  onClick={() => setShowCommodityModal(true)}
                  className="flex items-center px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <FiPackage className="mr-1" />
                  Manage Commodities
                </button>
                <button
                  onClick={() => onEdit(currentActivity)}
                  className="flex items-center px-3 py-2 text-sm bg-yellowcolor text-black rounded-lg hover:bg-yellowcolor/90 transition-colors"
                >
                  <FiEdit2 className="mr-1" />
                  Edit Activity
                </button>
              </div>}

              <div className="flex space-x-2">
                <button
                  onClick={() => setShowConfirmModal(true)}
                  className="flex items-center px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <FiTrash2 className="mr-1" />
                  Delete Activity
                </button>
                <button
                  onClick={onClose}
                  className="px-3 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-dark dark:text-gray-300 dark:hover:bg-gray-600 rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Commodity Management Modal */}
      {showCommodityModal && (
        <CommodityActivityModal
          activity={currentActivity}
          onClose={() => setShowCommodityModal(false)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showConfirmModal && (
        <DeleteModal
          setShowConfirmModal={setShowConfirmModal}
          showConfirmModal={showConfirmModal}
          title="activity"
          loading={deleteLoading}
          handleDelete={handleDeleteActivity}
        />
      )}

      {/* Farmer Detail Drawer */}
      <FarmerDetailDrawer
        isOpen={showFarmerDrawer}
        setIsOpen={setShowFarmerDrawer}
        farmer={selectedFarmer}
      />
    </div>
  )
}

export default ActivityDetail
